/**
 * 权限点的人话。
 *
 * ★ 为什么要单独一份:配角色的人是运营主管,不是写代码的。
 *   给他一屏 `order:view_pii`、`driver:policy_manage`,他能做的
 *   只有「全选」——而全选正是权限体系失效的方式。
 *
 * ★ 每一条除了名字还有一句**「给了会怎样」**。危险的那几个
 *   (看收件人电话、改价、付款、回滚导入)光看名字看不出轻重:
 *   「订单-导出」听起来像个报表功能,而它导出来的文件里
 *   带着几百个收件人的姓名、电话和门牌号,还会被转发到微信群里。
 */
import { P, type PermissionCode } from './permissions';

/** 模块的中文名 —— 界面上按这个分组 */
export const MODULE_LABELS: Record<string, string> = {
  order: '订单',
  zone: '配送区域',
  pricing: '计价',
  pickup_point: '取货点',
  contact: '联系人',
  customer: '商家',
  driver: '司机',
  shift: '排班',
  pool: '抢单池',
  import: '批量导入',
  integration: '三方对接',
  settlement: '结算',
  user: '用户',
  role: '角色',
  audit: '审计',
  analytics: '数据分析',
  setting: '系统设置',
  tenant: '租户',
};

export interface PermissionLabel {
  /** 中文名,一行放得下 */
  name: string;
  /** 给了会怎样 —— 只写后果,不复述名字 */
  hint?: string;
}

export const PERMISSION_LABELS: Record<PermissionCode, PermissionLabel> = {
  // ── 订单 ──
  [P.ORDER_READ]: { name: '查看订单' },
  [P.ORDER_CREATE]: { name: '新建订单' },
  [P.ORDER_UPDATE]: { name: '修改订单', hint: '包括改地址、件数、代收金额' },
  [P.ORDER_CANCEL]: { name: '取消订单' },
  [P.ORDER_ASSIGN]: { name: '派单给司机' },
  [P.ORDER_TRANSFER]: { name: '转派', hint: '把已经在别人手上的单转给另一个司机' },
  [P.ORDER_UNASSIGN]: { name: '收回派单' },
  [P.ORDER_ACCEPT]: { name: '接单', hint: '司机端用' },
  [P.ORDER_GRAB]: { name: '抢单', hint: '司机端用' },
  [P.ORDER_PICKUP]: { name: '取货', hint: '司机端用' },
  [P.ORDER_DELIVER]: { name: '送达', hint: '司机端用' },
  [P.ORDER_FAIL]: { name: '标记异常', hint: '司机端用:没人签收、地址错、拒收' },
  [P.ORDER_OVERRIDE_PROOF]: {
    name: '无照片也能记取货 / 送达',
    hint: '★ 绕开商家要求的拍照凭证 —— 给了这个权限的人,填一句理由就能把没有照片的单记成已送达。事后时间轴上写明「无照片凭证」',
  },
  [P.ORDER_EXPORT]: {
    name: '导出订单',
    hint: '★ 导出的文件里有几百个收件人的姓名、电话、门牌号,而文件会被转发',
  },
  [P.ORDER_VIEW_PII]: {
    name: '看收件人联系方式',
    hint: '★ 没有这一项时电话和门牌是打码的 —— 这是最该省着给的一项',
  },
  [P.ORDER_VIEW_PRICE]: { name: '看运费', hint: '客户被收了多少' },
  [P.ORDER_VIEW_PAYOUT]: { name: '看司机酬劳', hint: '司机这一单挣多少 —— 和运费是两件事' },
  [P.ORDER_REPRICE]: {
    name: '重算运费',
    hint: '★ 同时改客户账单和司机收入两件事',
  },
  [P.ANALYTICS_READ]: {
    name: '看数据分析',
    hint: '★ 报表是全租户汇总的 —— 只看得到自己片区的人,给了这一项就看得到全公司的经营数字',
  },

  // ── 配送区域 ──
  [P.ZONE_READ]: { name: '查看区域' },
  [P.ZONE_MANAGE]: { name: '管理区域', hint: '建区域、改加价' },
  [P.ZONE_KEYWORD_MANAGE]: {
    name: '管理区域关键词',
    hint: '改了会影响以后每一单落到哪个区、按哪个价算',
  },

  // ── 计价 ──
  [P.PRICING_READ]: { name: '查看计价规则' },
  [P.PRICING_MANAGE]: {
    name: '管理计价规则',
    hint: '★ 改完之后每一单的价钱都跟着变,而对账时才看得出来',
  },

  // ── 取货点 ──
  [P.PICKUP_POINT_READ]: { name: '查看取货点' },
  [P.PICKUP_POINT_MANAGE]: { name: '管理取货点', hint: '含围栏范围 —— 关了司机在哪都能点取货' },

  // ── 联系人 ──
  [P.CONTACT_READ]: { name: '查看联系人', hint: '收件人的历史地址和电话' },
  [P.CONTACT_MANAGE]: { name: '管理联系人' },

  // ── 商家 ──
  [P.CUSTOMER_READ]: { name: '查看商家' },
  [P.CUSTOMER_MANAGE]: { name: '管理商家', hint: '含取货拍照要求、分区基础价' },

  // ── 司机 ──
  [P.DRIVER_READ]: { name: '查看司机' },
  [P.DRIVER_MANAGE]: { name: '管理司机', hint: '★ 含建号和重置密码 —— 等于能拿到别人的账号' },
  [P.DRIVER_VERIFY]: { name: '审核司机资料', hint: '驾照、行驶证' },
  [P.DRIVER_POLICY_MANAGE]: { name: '管理司机规则', hint: '接单上限、服务区域、抢单资格' },
  [P.DRIVER_LOCATION_VIEW]: { name: '看司机位置', hint: '★ 实时定位,属于对员工的监控' },

  // ── 排班 ──
  [P.SHIFT_READ]: { name: '查看排班' },
  [P.SHIFT_MANAGE]: { name: '管理排班' },

  // ── 抢单池 ──
  [P.POOL_CONFIG_MANAGE]: { name: '配置抢单池', hint: '谁能抢、抢多久过期' },

  // ── 批量导入 ──
  [P.IMPORT_CREATE]: { name: '上传导入文件' },
  [P.IMPORT_CONFIRM]: { name: '确认导入', hint: '一次落几百单进系统' },
  [P.IMPORT_ROLLBACK]: { name: '回滚导入', hint: '★ 一次撤掉整批单,已经派出去的也会受影响' },

  // ── 三方对接 ──
  [P.INTEGRATION_READ]: { name: '查看对接通道' },
  [P.INTEGRATION_MANAGE]: { name: '管理对接通道', hint: '★ 改的是往外发数据的地址' },
  [P.INTEGRATION_REPLAY]: { name: '重放回调', hint: '重复触发一次三方通知' },

  // ── 结算 ──
  [P.SETTLEMENT_READ]: { name: '查看结算单' },
  [P.SETTLEMENT_MANAGE]: { name: '管理结算单', hint: '生成、调整' },
  [P.SETTLEMENT_PAY]: {
    name: '确认打款',
    hint: '★ 钱真的出去那一步 —— 故意和「管理结算单」分开,一个人算、另一个人付',
  },

  // ── 用户 / 角色 ──
  [P.USER_READ]: { name: '查看用户' },
  [P.USER_MANAGE]: {
    name: '管理用户',
    hint: '★ 建号、停用、重置密码 —— 有这一项就能给自己开一个更大的号',
  },
  [P.ROLE_MANAGE]: {
    name: '管理角色',
    hint: '★ 能改每个角色有哪些权限 —— 等于能给自己加任何权限',
  },

  // ── 其它 ──
  [P.AUDIT_READ]: { name: '查看审计日志', hint: '谁在什么时候改了什么' },
  [P.SETTING_MANAGE]: { name: '管理系统设置', hint: '功能开关、地图、短信、对象存储' },
  [P.TENANT_MANAGE]: { name: '管理租户', hint: '时区、货币、单号前缀' },
};

/** 界面上一眼要标红的那几个 —— 和 hint 里的 ★ 是同一批 */
export function isDangerous(code: PermissionCode): boolean {
  return PERMISSION_LABELS[code]?.hint?.startsWith('★') ?? false;
}

/** 没登记的权限点也要能显示 —— 显示成代码本身,而不是空白 */
export function permissionLabelOf(code: string): PermissionLabel {
  return PERMISSION_LABELS[code as PermissionCode] ?? { name: code };
}
